import { useState, type FormEvent } from 'react';
import { setBreakEvenConfig } from '../../api/dashboard';
import { getErrorMessage } from '../../api/client';
import { Icon } from '../ui/Icon';
import { Button, Dialog, Field, Input } from '../ui/primitives';
import type { BreakEvenConfigView } from '../../types/dashboard';
import { formatMoney } from '../../utils/ownerOverview';

const FORM_ID = 'inicio-break-even-form';

/**
 * Rediseño · Bloque C — la hoja «Punto de equilibrio» (P-021): tres datos y, en vivo, cuánto hay que
 * vender al día. Es la configuración que ya existía, con la piel nueva; guarda con la misma llamada.
 *
 * La validación es la del servidor, no la de la maqueta: gastos fijos MAYORES a cero (permitir $0 es
 * un ajuste futuro: B0-13, FUTURE_UPGRADES #39), días enteros de 1 a 31 y margen mayor a 0 y hasta 100.
 * La meta que se ve aquí es una vista previa con lo que la persona escribe; la que cuenta es la que
 * devuelve el servidor al guardar.
 *
 * Solo la abre el dueño: es el único a quien el servidor deja guardarla.
 */
export function BreakEvenDialog({ current, onClose, onSaved }: { current: BreakEvenConfigView; onClose: () => void; onSaved: () => void }) {
  const [fixed, setFixed] = useState(current.configured ? String(current.monthlyFixedCosts) : '');
  const [days, setDays] = useState(current.configured ? String(current.daysOpenPerMonth) : '');
  const [margin, setMargin] = useState(current.configured ? String(current.grossMarginPercent) : '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const fixedValue = Number(fixed);
  const daysValue = Number(days);
  const marginValue = Number(margin);
  const fixedOk = fixed.trim() !== '' && Number.isFinite(fixedValue) && fixedValue > 0;
  const daysOk = days.trim() !== '' && Number.isInteger(daysValue) && daysValue >= 1 && daysValue <= 31;
  const marginOk = margin.trim() !== '' && Number.isFinite(marginValue) && marginValue > 0 && marginValue <= 100;
  const ready = fixedOk && daysOk && marginOk;
  // Un campo vacío todavía no es un error: se avisa cuando lo escrito no sirve.
  const fixedError = fixed.trim() !== '' && !fixedOk ? 'Tus gastos fijos del mes deben ser mayores a $0.' : undefined;
  const daysError = days.trim() !== '' && !daysOk ? 'Los días abiertos deben ser un número entero entre 1 y 31.' : undefined;
  const marginError = margin.trim() !== '' && !marginOk ? 'El margen debe ser mayor a 0 y máximo 100.' : undefined;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSaving || !ready) return;
    setIsSaving(true);
    setError(undefined);
    try {
      await setBreakEvenConfig({ monthlyFixedCosts: fixedValue, daysOpenPerMonth: daysValue, grossMarginPercent: marginValue });
      onSaved();
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar la configuración.'));
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      description="Lo que necesitas vender al día para cubrir tus gastos fijos."
      footer={<>
        <Button disabled={isSaving} onClick={onClose}>Cancelar</Button>
        <Button disabled={!ready} form={FORM_ID} loading={isSaving} loadingLabel="Guardando…" type="submit" variant="primary">Guardar</Button>
      </>}
      isBusy={isSaving}
      onClose={onClose}
      title="Punto de equilibrio"
    >
      <form className="n3-inicio__form" id={FORM_ID} noValidate onSubmit={(event) => void submit(event)}>
        {error && (
          <p className="n3-inicio__form-error" role="alert"><Icon name="warning" size={16} /><span>{error}</span></p>
        )}
        <Field error={fixedError} hint="Todo lo que pagas al mes aunque no vendas nada: renta, sueldos, luz…" label="Gastos fijos del mes (MXN)">
          <Input inputMode="decimal" min="0" onChange={(event) => setFixed(event.target.value)} prefix="$" step="any" type="number" value={fixed} />
        </Field>
        <Field error={daysError} label="Días abiertos al mes">
          <Input inputMode="numeric" max="31" min="1" onChange={(event) => setDays(event.target.value)} step="1" suffix="días" type="number" value={days} />
        </Field>
        <Field error={marginError} hint="De cada $100 vendidos, cuánto te queda después del costo de lo que vendes." label="Margen estimado (%)">
          <Input inputMode="decimal" max="100" min="0" onChange={(event) => setMargin(event.target.value)} step="any" suffix="%" type="number" value={margin} />
        </Field>
        <div aria-live="polite" className={ready ? 'n3-inicio__preview' : 'n3-inicio__preview n3-inicio__preview--off'}>
          {ready ? (
            <>
              <p className="n3-inicio__preview-text">Necesitas vender <strong>{formatMoney(fixedValue / daysValue / (marginValue / 100))}</strong> al día para cubrir tus gastos.</p>
              <p className="n3-inicio__preview-how">{formatMoney(fixedValue)} ÷ {daysValue} días = {formatMoney(fixedValue / daysValue)} al día · ÷ {marginValue} % de margen</p>
            </>
          ) : (
            <p className="n3-inicio__preview-text">Completa los tres datos para ver el resultado.</p>
          )}
        </div>
      </form>
    </Dialog>
  );
}
